import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

export interface IFeatureFlagsQueryParams {
	hotelId?: number;
}

/**
 * Feature-flag map returned by the BE under
 * `IFeatureFlagsResponse.flags`. Each key is a server-driven toggle
 * the FE consults at render time. Add new flags here as the BE
 * exposes them. Most values are booleans; `blockoutFeature` is a JSON
 * flag whose variations are booleans.
 */
export interface IBlockoutFeatureFlags {
	isRenameLabelsToBlockoutNight: boolean;
	isStayEndCanBeBlockoutNight: boolean;
}

export interface IFeatureFlags {
	isMyAuditTrendsPreviousPeriodEnabled: boolean;
	/**
	 * Master gate for the entire new admin-roster experience. When this
	 * is false, every other `isNewAdminRoster*` toggle below MUST be
	 * treated as false too — consumers should AND-gate the children with
	 * this flag rather than reading them independently. See
	 * `roster-detail-drawer.tsx` for the canonical pattern.
	 */
	isNewAdminRosterEnabled: boolean;
	isNewAdminRosterOverviewDetailsEnabled: boolean;
	isNewAdminRosterOverviewTabEnabled: boolean;
	isNewAdminRosterOverviewApprovalActionsEnabled: boolean;
	isNewAdminRosterLogsEnabled: boolean;
	isMyAuditTrendsByTaggedEnabled: boolean;
	isMyAuditOverviewEnabled: boolean;
	isMyAuditDetailsEnabled: boolean;
	isUserSettingsEnabled: boolean;
	isMyLqaAdministrationCustomPricingEnabled: boolean;
	isMyLqaAdministrationDefaultPricingEnabled: boolean;
	isMyLqaAdministrationContractsEnabled: boolean;
	blockoutFeature?: IBlockoutFeatureFlags;
}

export interface IFeatureFlagsResponse {
	flags: IFeatureFlags;
	/**
	 * Echoed user / tenant context the BE used when evaluating the
	 * flags. Shape varies per call site and is not consumed on the
	 * FE today; typed as `unknown` to keep callers from depending on
	 * a specific shape.
	 */
	userContext: unknown;
	cached: boolean;
}

type FeatureFlagsQueryOptions = Omit<
	UseQueryOptions<
		AxiosResponse<IFeatureFlagsResponse>,
		AxiosError<CoreApiError>,
		IFeatureFlagsResponse
	>,
	'queryKey' | 'queryFn' | 'select'
>;

export function useFeatureFlags(
	params?: IFeatureFlagsQueryParams,
	queryOptions?: FeatureFlagsQueryOptions
) {
	return useQuery<
		AxiosResponse<IFeatureFlagsResponse>,
		AxiosError<CoreApiError>,
		IFeatureFlagsResponse
	>({
		queryKey: coreApiQueryKey(endpoints.featureFlags.check, 'get', params),
		queryFn: async () =>
			coreApiClient.get<IFeatureFlagsResponse>(endpoints.featureFlags.check, {
				params,
			}),
		select: (response) => response.data,
		...queryOptions,
	});
}
